import { Fragment, useCallback, useEffect, useState } from "react";
import type { RotaDay, RotaStatus, TimeOffRequest } from "@dentacore/shared-types";

import { useBranchTimezone } from "../api/use-branch-timezone";
import { wallClockOfInstant } from "../calendar/zoned-time";
import { Pager, useHeight } from "../ui/paging";
import menus from "../ui/popovers.module.css";
import { Popover } from "./popover";
import {
  addDays,
  datesOf,
  dayFor,
  dayStateOf,
  fractionOf,
  isIn,
  longDate,
  minutesOf,
  onLeaveOn,
  roleLabel,
  spanOf,
} from "./roster";
import type { DayState, Person } from "./roster";
import styles from "./staff.module.css";
import { KIND_LABELS } from "./weekdays";
import { mondayOf, weekLabel } from "./weeks";

/** Every row on the day is this tall, which is what lets a page be counted. */
const ROW = 46;

/**
 * The schedule: one card for one day (ST9, ST10).
 *
 * **The top line is only the date.** The week it belongs to opens from it —
 * seven days, each saying how many are in — so moving a day or a week is one
 * or two presses without a strip of seven dates sitting over the card. Search
 * and the week's own **⋯** (publication and changes, admins) sit at the end.
 *
 * **Who is in is a timeline**: a person a row, their shift a bar across the
 * hours in their role's colour. The rows page to fit the card (PF4); who is
 * off, on leave or at another location is counted at the foot and added to
 * the list on request, so nobody is out of reach and nobody crowds the day.
 */
export function DayCard({
  day,
  today,
  people,
  approved,
  loading,
  publication,
  exceptionTotal,
  isAdmin,
  busy,
  selectedId,
  showLocations = false,
  onDay,
  onSelect,
  onPublish,
  onUnpublish,
}: {
  day: string;
  today: string;
  people: readonly Person[];
  approved: readonly TimeOffRequest[];
  /** True until this location's week has arrived. */
  loading: boolean;
  publication: RotaStatus | null;
  /** Days changed this week only, across everybody here. */
  exceptionTotal: number;
  isAdmin: boolean;
  busy: boolean;
  selectedId: string | null;
  /** Name each person's locations under them — the clinic has two or more (EB3). */
  showLocations?: boolean;
  onDay: (date: string) => void;
  onSelect: (userId: string) => void;
  onPublish: () => void;
  onUnpublish: () => void;
}): React.JSX.Element {
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState("");
  const [showOff, setShowOff] = useState(false);
  const [showAway, setShowAway] = useState(false);
  const [page, setPage] = useState(0);
  const [areaRef, areaHeight] = useHeight<HTMLDivElement>();
  const now = useNowMinutes();

  const needle = query.trim().toLowerCase();
  useEffect(() => {
    setPage(0);
  }, [day, needle, showOff, showAway]);

  const weekStart = mondayOf(day);
  const dates = datesOf(weekStart);
  const isToday = day === today;

  /** A week withheld from this reader (unpublished, S6) — said, never drawn as empty. */
  const withheld = publication !== null && !publication.visible;

  const stateOn = (person: Person, date: string): DayState | null => {
    const theirs = person.week?.days.find((row) => row.date === date);
    return theirs === undefined ? null : dayStateOf(theirs, onLeaveOn(person.id, date, approved));
  };

  const here = people.filter((person) => person.week !== null && person.week.days.length === 7);
  const inDay = here
    .filter((person) => isIn(stateOn(person, day) ?? "off"))
    .sort(
      (a, b) =>
        byStart(dayFor(a, day), dayFor(b, day)) ||
        compare(a.role, b.role) ||
        compare(a.name, b.name),
    );
  const outDay = here.filter((person) => !isIn(stateOn(person, day) ?? "off"));
  const onLeave = outDay.filter((person) => stateOn(person, day) === "leave");
  const offCount = outDay.length - onLeave.length;
  const away = people.filter((person) => person.week === null && person.active);

  const rows =
    needle !== ""
      ? people.filter((person) => person.name.toLowerCase().includes(needle))
      : [...inDay, ...(showOff ? outDay : []), ...(showAway ? away : [])];

  // The span is the shifts actually drawn, so the bars use the card's width.
  const span = spanOf(
    rows
      .filter((person) => isIn(stateOn(person, day) ?? "off"))
      .map((person) => dayFor(person, day))
      .filter((row): row is RotaDay => row !== null),
  );

  const counts = dates.map(
    (date) => here.filter((person) => isIn(stateOn(person, date) ?? "off")).length,
  );

  const perPage = areaHeight > 0 ? Math.max(1, Math.floor(areaHeight / ROW)) : rows.length || 1;
  const pageCount = Math.max(1, Math.ceil(rows.length / perPage));
  const current = Math.min(page, pageCount - 1);
  const shown = rows.slice(current * perPage, current * perPage + perPage);

  const ticks: number[] = [];
  const lines: number[] = [];
  if (span !== null) {
    for (let hour = span.from; hour <= span.to; hour += 2) ticks.push(hour);
    for (let hour = span.from; hour <= span.to; hour += 1) lines.push(hour);
  }
  const nowAt =
    isToday && span !== null && needle === "" && now >= span.from * 60 && now <= span.to * 60
      ? (now - span.from * 60) / ((span.to - span.from) * 60)
      : null;

  const at = (hour: number): string =>
    `${String(((hour - (span?.from ?? 0)) / ((span?.to ?? 1) - (span?.from ?? 0))) * 100)}%`;

  let body: React.ReactNode;
  if (loading) body = <p className={styles.blank}>Reading&hellip;</p>;
  else if (withheld)
    body = (
      <p className={styles.empty} data-testid="week-unpublished">
        This week&rsquo;s rota has not been published yet.
      </p>
    );
  else if (rows.length === 0)
    body = (
      <p className={styles.empty} data-testid="day-empty">
        {needle !== ""
          ? "Nobody by that name."
          : here.length === 0
            ? "Nobody works at this location yet. People are added in System."
            : `Nobody is in on ${longDate(day).split(" ")[0] ?? ""}.`}
      </p>
    );
  else
    body = (
      <>
        {span !== null && (
          <div className={styles.lines} aria-hidden="true">
            <span />
            <span className={styles.linesTrack}>
              {lines.map((hour) => (
                <span key={hour} className={styles.gridLine} style={{ left: at(hour) }} />
              ))}
              {nowAt !== null && (
                <span
                  className={styles.nowLine}
                  style={{ left: `${String(nowAt * 100)}%` }}
                  data-testid="now-line"
                />
              )}
            </span>
          </div>
        )}
        {shown.map((person) => (
          <button
            key={person.id}
            type="button"
            className={person.id === selectedId ? `${styles.tRow} ${styles.tRowOn}` : styles.tRow}
            data-card=""
            aria-current={person.id === selectedId ? "true" : undefined}
            onClick={() => onSelect(person.id)}
            data-ring="hr.person"
            data-ring-id={person.id}
            data-testid="person-row"
          >
            <span className={styles.tWho}>
              <span className={styles.tName}>{person.name}</span>
              {showLocations && person.locations.length > 0 && (
                <span
                  className={styles.tPlaces}
                  title={person.locations.join(" · ")}
                  data-testid="person-locations"
                >
                  {/* Where this day is worked, when known (OQ-D17); else every location. */}
                  {dayFor(person, day)?.locationName ?? person.locations.join(" · ")}
                </span>
              )}
            </span>
            <span className={styles.track}>
              <Bar
                person={person}
                row={dayFor(person, day)}
                state={stateOn(person, day)}
                span={span}
              />
            </span>
          </button>
        ))}
      </>
    );

  return (
    <section className={styles.dayCard} data-testid="day-card">
      <div className={styles.dayBar}>
        <button
          type="button"
          className={styles.step}
          aria-label="Previous day"
          onClick={() => onDay(addDays(day, -1))}
          data-testid="day-prev"
        >
          &lsaquo;
        </button>

        <Popover
          buttonClassName={styles.dateButton}
          button={
            <>
              {isToday ? "Today, " : ""}
              {longDate(day)}
              <span className={styles.caret} aria-hidden="true">
                ▾
              </span>
            </>
          }
          align="start"
          panelClassName={styles.weekPop}
          testId="day-label"
          panelTestId="week-pop"
        >
          {(close) => (
            <>
              <span className={styles.popHead} data-testid="week-label">
                {weekLabel(weekStart)}
              </span>
              {dates.map((date, index) => (
                <button
                  key={date}
                  type="button"
                  className={
                    date === day
                      ? `${menus.item} ${styles.popDay} ${styles.popDayOn}`
                      : `${menus.item} ${styles.popDay}`
                  }
                  onClick={() => {
                    close();
                    onDay(date);
                  }}
                  data-testid="day-pick"
                >
                  <span>
                    {longDate(date)}
                    {date === today && " · today"}
                  </span>
                  <span className={styles.popCount}>
                    {loading || withheld ? "–" : `${String(counts[index] ?? 0)} in`}
                  </span>
                </button>
              ))}
              <span className={styles.popFoot}>
                <button
                  type="button"
                  className={styles.linkish}
                  onClick={() => onDay(addDays(day, -7))}
                  data-testid="week-prev"
                >
                  &lsaquo; Previous week
                </button>
                <button
                  type="button"
                  className={styles.linkish}
                  onClick={() => onDay(addDays(day, 7))}
                  data-testid="week-next"
                >
                  Next week &rsaquo;
                </button>
              </span>
            </>
          )}
        </Popover>

        <button
          type="button"
          className={styles.step}
          aria-label="Next day"
          onClick={() => onDay(addDays(day, 1))}
          data-testid="day-next"
        >
          &rsaquo;
        </button>

        {/* A way back, offered only away from it — a button already where it goes is noise. */}
        {!isToday && (
          <button
            type="button"
            className={styles.quiet}
            onClick={() => onDay(today)}
            data-testid="day-today"
          >
            Today
          </button>
        )}

        {!loading && !withheld && (
          <span className={styles.dayCount} data-testid="day-count">
            {inDay.length} in
          </span>
        )}

        <span className={styles.barEnd}>
          {/* Only while it is one: a published week is the ordinary case. */}
          {isAdmin && publication !== null && !publication.published && (
            <span className={styles.draftTag} data-testid="draft-tag">
              Draft
            </span>
          )}

          {searching || query !== "" ? (
            <>
              <input
                type="search"
                className={styles.search}
                placeholder="Search people"
                aria-label="Search people"
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                data-testid="people-search"
              />
              <button
                type="button"
                className={styles.icon}
                aria-label="Close search"
                onClick={() => {
                  setSearching(false);
                  setQuery("");
                }}
                data-testid="people-search-close"
              >
                &times;
              </button>
            </>
          ) : (
            <button
              type="button"
              className={styles.icon}
              aria-label="Search people"
              onClick={() => setSearching(true)}
              data-testid="people-search-open"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M10.5 17a6.5 6.5 0 1 1 0-13 6.5 6.5 0 0 1 0 13zM15.5 15.5 20 20" />
              </svg>
            </button>
          )}

          {/*
            **Publishing is an admin's control and nobody else's** (S6/S3), and
            it is done once a week — so it waits behind ⋯ rather than sitting
            beside the arrows that are pressed all day.
          */}
          {isAdmin && publication !== null && (
            <Popover
              label="The week"
              buttonClassName={menus.dots}
              button="⋯"
              align="end"
              panelClassName={styles.weekMenu}
              testId="week-menu"
            >
              {(close) => (
                <>
                  <span className={styles.menuNote} data-testid="publish-state">
                    {weekLabel(weekStart)} · {publication.published ? "published" : "draft"} ·{" "}
                    <span data-testid="week-tag">
                      {exceptionTotal === 1 ? "1 change" : `${String(exceptionTotal)} changes`}
                    </span>
                  </span>
                  <button
                    type="button"
                    className={menus.item}
                    disabled={busy}
                    onClick={() => {
                      close();
                      if (publication.published) onUnpublish();
                      else onPublish();
                    }}
                    data-testid={publication.published ? "week-unpublish" : "week-publish"}
                  >
                    {publication.published ? "Back to draft" : "Publish this week"}
                  </button>
                </>
              )}
            </Popover>
          )}
        </span>
      </div>

      <div className={styles.axis} aria-hidden="true">
        <span />
        <span className={styles.axisTrack}>
          {!withheld &&
            ticks.map((hour) => (
              <span key={hour} className={styles.tick} style={{ left: at(hour) }}>
                {String(hour).padStart(2, "0")}:00
              </span>
            ))}
        </span>
      </div>

      <div className={styles.dayBody} ref={areaRef} data-testid="day-timeline">
        {body}
      </div>

      <div className={styles.dayFoot}>
        {!loading && !withheld && needle === "" && (
          <>
            {onLeave.length > 0 && (
              <span className={styles.leaveNames}>
                On leave:{" "}
                {onLeave.map((person, index) => (
                  <Fragment key={person.id}>
                    {index > 0 && ", "}
                    <button
                      type="button"
                      className={styles.leaveLink}
                      onClick={() => onSelect(person.id)}
                      data-testid="leave-link"
                    >
                      {person.name}
                    </button>
                  </Fragment>
                ))}
              </span>
            )}
            {offCount > 0 && (
              <button
                type="button"
                className={styles.linkish}
                aria-pressed={showOff}
                onClick={() => setShowOff((was) => !was)}
                data-testid="show-off"
              >
                {showOff ? "Hide who is off" : `${String(offCount)} off`}
              </button>
            )}
            {away.length > 0 && (
              <button
                type="button"
                className={styles.linkish}
                aria-pressed={showAway}
                onClick={() => setShowAway((was) => !was)}
                data-testid="show-away"
              >
                {showAway ? "Hide other locations" : `${String(away.length)} at other locations`}
              </button>
            )}
          </>
        )}
        <span className={styles.footPager}>
          <Pager
            page={current}
            count={pageCount}
            onPage={setPage}
            from={current * perPage + 1}
            to={current * perPage + shown.length}
            total={rows.length}
            noun="people"
            testId="day-pager"
          />
        </span>
      </div>
    </section>
  );
}

/** What a row draws in its track: the shift, or a word for why there is none. */
function Bar({
  person,
  row,
  state,
  span,
}: {
  person: Person;
  row: RotaDay | null;
  state: DayState | null;
  span: { from: number; to: number } | null;
}): React.JSX.Element {
  if (!person.active) return <span className={styles.quietBar}>Cannot be scheduled</span>;
  if (person.week === null)
    return (
      <span className={styles.quietBar}>
        {person.elsewhere.length > 0 ? `At ${person.elsewhere.join(", ")}` : "Not at this location"}
      </span>
    );
  if (row === null || state === null)
    return <span className={styles.quietBar}>Not published yet</span>;
  if (state === "leave")
    return <span className={`${styles.quietBar} ${styles.quietLeave}`}>On leave</span>;
  if (state === "off" || span === null || row.startsAt === null || row.endsAt === null)
    return <span className={styles.quietBar}>Off</span>;

  const from = fractionOf(row.startsAt, span);
  const to = fractionOf(row.endsAt, span);
  const kind = KIND_LABELS[row.kind] ?? "";
  const words = `${kind} ${row.startsAt}–${row.endsAt}${row.note !== null && row.note !== "" ? ` · ${row.note}` : ""}`;

  return (
    <span
      className={row.kind === "TRAINING" ? `${styles.shift} ${styles.shiftTraining}` : styles.shift}
      data-role={person.role}
      style={{ left: `${String(from * 100)}%`, width: `${String((to - from) * 100)}%` }}
      title={`${roleLabel(person.role)} · ${words}${row.fromException ? " · changed this week" : ""}`}
      data-testid="shift"
    >
      {row.fromException && <span className={styles.changedDot} aria-label="changed this week" />}
      {row.kind !== "CLINIC" && <span className={styles.visuallyHidden}>{kind} </span>}
      {row.startsAt}–{row.endsAt}
    </span>
  );
}

/** Earliest start first; a day with no start sorts last. */
function byStart(a: RotaDay | null, b: RotaDay | null): number {
  return minutesOf(a?.startsAt ?? "99:99") - minutesOf(b?.startsAt ?? "99:99");
}

function compare(a: string, b: string): number {
  return a.localeCompare(b);
}

/** Minutes since midnight, refreshed each minute for the line at now. */
function useNowMinutes(): number {
  // The location's clock, as the rota is drawn in it (P2 Q8).
  const timezone = useBranchTimezone();
  const read = useCallback((): number => {
    const [hours, minutes] = wallClockOfInstant(new Date(), timezone).clock.split(":").map(Number);
    return (hours ?? 0) * 60 + (minutes ?? 0);
  }, [timezone]);
  const [now, setNow] = useState(read);
  useEffect(() => {
    setNow(read());
    const timer = window.setInterval(() => setNow(read()), 60_000);
    return () => window.clearInterval(timer);
  }, [read]);
  return now;
}
